'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import {
  FieldGrid,
  FieldGroup,
  FieldHint,
  FieldLabel,
  FormActions,
  FormPanel,
  FormSection,
  PrimaryButton,
  SelectField,
  TextareaField,
  TextField,
} from '@/app/FormFields'

type Project = { id: string; name: string }

type IntakeConfig = {
  departments: string[]
  issue_categories: string[]
  affected_systems: string[]
  action_requested_options: string[]
}

const LAST_PROJECT_KEY = 'ask-qien-last-project'
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'] as const
const SUCCESS_CRITERIA_MAX = 500

function getTodayDateInputValue() {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

type SubmittedTicket = {
  id: string
  referenceNumber: string
}

type PollStatus =
  | 'SUBMITTED'
  | 'NEEDS_INFO'
  | 'TRIAGED'
  | 'AWAITING_EXPERT'
  | 'EXPERT_REVIEWING'
  | 'RESPONSE_DRAFTING'
  | 'RESPONSE_READY'
  | 'RESPONDED'
  | 'AWAITING_REPLY'
  | 'CLOSED'
  | 'ARCHIVED'

const TERMINAL_STATUSES: PollStatus[] = [
  'TRIAGED', 'AWAITING_EXPERT', 'EXPERT_REVIEWING', 'RESPONSE_DRAFTING',
  'RESPONSE_READY', 'RESPONDED', 'AWAITING_REPLY', 'CLOSED', 'ARCHIVED',
]

function getStoredProject(projects: Project[]): Project | null {
  if (typeof window === 'undefined') return null

  try {
    const last = window.localStorage.getItem(LAST_PROJECT_KEY)
    if (!last) return null

    const parsed = JSON.parse(last) as Project
    return projects.find((p) => p.id === parsed.id) ?? null
  } catch {
    return null
  }
}

// --- Post-submission polling view ---

function SkeletonLine({ width = 'w-full' }: { width?: string }) {
  return <div className={`h-3 ${width} rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse`} />
}

function PostSubmissionView({
  ticket,
  waitingBody,
  needsInfoBody,
  completeBody,
}: {
  ticket: SubmittedTicket
  waitingBody: string
  needsInfoBody: string
  completeBody: string
}) {
  const [pollStatus, setPollStatus] = useState<PollStatus>('SUBMITTED')
  const [questions, setQuestions] = useState<string[]>([])
  const [answers, setAnswers] = useState<string[]>([])
  const [answerLoading, setAnswerLoading] = useState(false)
  const [answerError, setAnswerError] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const [pollTimedOut, setPollTimedOut] = useState(false)

  useEffect(() => {
    if (answered) return
    if (TERMINAL_STATUSES.includes(pollStatus)) return
    if (pollStatus === 'NEEDS_INFO') return

    let attempts = 0
    const MAX_ATTEMPTS = 30 // 30 × 2s = 60s max

    const interval = setInterval(async () => {
      attempts++
      try {
        const res = await fetch(`/api/tickets/${ticket.id}/poll`)
        if (!res.ok) return
        const data = (await res.json()) as { status: PollStatus; questions: string[] }
        setPollStatus(data.status)
        if (data.questions?.length) {
          setQuestions(data.questions)
          setAnswers(data.questions.map(() => ''))
        }
      } catch {
        // ignore transient fetch errors
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(interval)
        setPollTimedOut(true)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [ticket.id, pollStatus, answered])

  function setAnswer(index: number, value: string) {
    setAnswers((prev) => prev.map((a, i) => (i === index ? value : a)))
  }

  const allAnswered = answers.length > 0 && answers.every((a) => a.trim().length > 0)

  async function handleAnswerSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!allAnswered) return
    setAnswerLoading(true)
    setAnswerError(null)
    const submissionText = questions
      .map((q, i) => `${i + 1}. ${q}\n\n${answers[i].trim()}`)
      .join('\n\n---\n\n')
    try {
      const res = await fetch(`/api/tickets/${ticket.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionText }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setAnswerError((data as { error?: string }).error ?? 'Something went wrong. Please try again.')
        return
      }
      setAnswered(true)
    } catch {
      setAnswerError('Something went wrong. Please try again.')
    } finally {
      setAnswerLoading(false)
    }
  }

  const isWaiting = pollStatus === 'SUBMITTED' && !pollTimedOut && !answered
  const isNeedsInfo = pollStatus === 'NEEDS_INFO' && !answered
  const isComplete = TERMINAL_STATUSES.includes(pollStatus) || answered || pollTimedOut

  return (
    <section className="space-y-4">
      {/* Ticket confirmed header */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 shadow-sm dark:border-emerald-400/30 dark:bg-emerald-400/10">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
          Ticket submitted
        </p>
        <h2 className="mt-2 text-xl font-semibold text-zinc-950 dark:text-white">
          {ticket.referenceNumber} is now in the queue.
        </h2>
      </div>

      {/* Waiting / loading state */}
      {isWaiting && (
        <div className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-sky-600 border-t-transparent animate-spin dark:border-sky-400" />
            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Evaluating your issue…</p>
          </div>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{waitingBody}</p>
          <div className="space-y-2">
            <SkeletonLine width="w-3/4" />
            <SkeletonLine width="w-full" />
            <SkeletonLine width="w-5/6" />
          </div>
        </div>
      )}

      {/* NEEDS_INFO — clarification questions */}
      {isNeedsInfo && questions.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-6 dark:border-amber-400/40 dark:bg-amber-300/10">
          <h3 className="text-sm font-semibold text-amber-950 dark:text-amber-100 mb-1">
            A few more details needed
          </h3>
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-5">{needsInfoBody}</p>
          <form onSubmit={handleAnswerSubmit} className="space-y-5">
            {questions.map((q, i) => (
              <FieldGroup key={i}>
                <FieldLabel htmlFor={`clarification-answer-${i}`} className="text-amber-900 dark:text-amber-100/90">
                  <span className="font-semibold">{i + 1}.</span> {q}
                </FieldLabel>
                <TextareaField
                  id={`clarification-answer-${i}`}
                  value={answers[i] ?? ''}
                  onChange={(e) => setAnswer(i, e.target.value)}
                  rows={6}
                  required
                  className="border-amber-400 focus:border-amber-500 focus:ring-amber-500/15 dark:border-amber-400/60 dark:focus:border-amber-300 dark:focus:ring-amber-300/15"
                  placeholder="Your answer…"
                />
              </FieldGroup>
            ))}
            {answerError && <p className="text-xs text-red-700 dark:text-red-300">{answerError}</p>}
            <PrimaryButton
              type="submit"
              disabled={answerLoading || !allAnswered}
            >
              {answerLoading ? 'Submitting…' : 'Submit additional information'}
            </PrimaryButton>
          </form>
        </div>
      )}

      {/* Complete / in-queue state */}
      {isComplete && (
        <div className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-sm text-zinc-700 dark:text-zinc-300">{completeBody}</p>
          <div className="mt-4">
            <Link
              href={`/tickets/${ticket.id}`}
              className="inline-block rounded-md border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
            >
              View ticket
            </Link>
          </div>
        </div>
      )}
    </section>
  )
}

// --- Main form ---

export default function SubmitForm({
  promptText,
  maxChars,
  intakeConfig,
  initialProjects,
  waitingBody,
  needsInfoBody,
  completeBody,
}: {
  promptText: string
  maxChars: number
  intakeConfig: IntakeConfig
  initialProjects: Project[]
  waitingBody: string
  needsInfoBody: string
  completeBody: string
}) {
  const projects = initialProjects
  const [storedProject] = useState(() => getStoredProject(projects))
  const [projectId, setProjectId] = useState(storedProject?.id ?? '')
  const [today] = useState(() => getTodayDateInputValue())

  const [department, setDepartment] = useState('')
  const [dateOfOccurrence, setDateOfOccurrence] = useState(today)
  const [issueCategory, setIssueCategory] = useState('')
  const [affectedSystem, setAffectedSystem] = useState('')
  const [userPriority, setUserPriority] = useState('')
  const [description, setDescription] = useState('')
  const [successCriteria, setSuccessCriteria] = useState('')
  const [actionRequested, setActionRequested] = useState('')

  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedTicket, setSubmittedTicket] = useState<SubmittedTicket | null>(null)

  const dateInFuture = Boolean(dateOfOccurrence && dateOfOccurrence > today)
  const descRemaining = maxChars - description.length
  const descOverLimit = descRemaining < 0
  const successRemaining = SUCCESS_CRITERIA_MAX - successCriteria.length
  const successOverLimit = successRemaining < 0

  const allFilled =
    projectId &&
    department &&
    dateOfOccurrence &&
    issueCategory &&
    affectedSystem &&
    userPriority &&
    description.trim() &&
    successCriteria.trim() &&
    actionRequested &&
    !dateInFuture &&
    !descOverLimit &&
    !successOverLimit

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!allFilled) {
      setSubmitted(true)
      const fieldOrder: Array<{ id: string; invalid: boolean }> = [
        { id: 'project-id', invalid: !projectId },
        { id: 'department', invalid: !department },
        { id: 'date-of-occurrence', invalid: !dateOfOccurrence || dateInFuture },
        { id: 'issue-category', invalid: !issueCategory },
        { id: 'affected-system', invalid: !affectedSystem },
        { id: 'user-priority', invalid: !userPriority },
        { id: 'action-requested', invalid: !actionRequested },
        { id: 'problem-description', invalid: !description.trim() },
        { id: 'success-criteria', invalid: !successCriteria.trim() },
      ]
      const first = fieldOrder.find((f) => f.invalid)
      if (first) document.getElementById(first.id)?.focus()
      return
    }
    setLoading(true)
    setError(null)

    const res = await fetch('/api/tickets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        submissionText: description,
        projectId,
        department,
        dateOfOccurrence,
        issueCategory,
        affectedSystem,
        userPriority: userPriority.toLowerCase(),
        successCriteria,
        actionRequested,
      }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      setError((data as { error?: string }).error ?? 'Something went wrong. Please try again.')
      setLoading(false)
      return
    }

    const { ticketId, referenceNumber } = await res.json()
    try {
      const selected = projects.find((p) => p.id === projectId)
      if (selected) localStorage.setItem(LAST_PROJECT_KEY, JSON.stringify(selected))
    } catch {}
    setSubmittedTicket({ id: ticketId, referenceNumber })
    setLoading(false)
  }

  if (submittedTicket) {
    return (
      <PostSubmissionView
        ticket={submittedTicket}
        waitingBody={waitingBody}
        needsInfoBody={needsInfoBody}
        completeBody={completeBody}
      />
    )
  }

  return (
    <FormPanel onSubmit={handleSubmit}>
      <FormSection>
        <FieldGroup>
          <FieldLabel htmlFor="project-id" required>Project</FieldLabel>
          <SelectField
            id="project-id"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            disabled={projects.length === 0}
            placeholderValue=""
            hasError={submitted && !projectId}
          >
            <option value="">{projects.length === 0 ? 'No active projects available' : 'Select project…'}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </SelectField>
          {submitted && !projectId && <FieldHint tone="error">Please select a project.</FieldHint>}
        </FieldGroup>

        <FieldGrid>
          <FieldGroup>
            <FieldLabel htmlFor="department" required>Department</FieldLabel>
            <SelectField id="department" value={department} onChange={(e) => setDepartment(e.target.value)} placeholderValue="" hasError={submitted && !department}>
              <option value="">Select department…</option>
              {intakeConfig.departments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </SelectField>
            {submitted && !department && <FieldHint tone="error">Please select a department.</FieldHint>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="date-of-occurrence" required>Date of Occurrence</FieldLabel>
            <TextField
              id="date-of-occurrence"
              type="date"
              value={dateOfOccurrence}
              onChange={(e) => setDateOfOccurrence(e.target.value)}
              max={today}
              hasError={submitted && (!dateOfOccurrence || dateInFuture)}
            />
            {submitted && dateInFuture && (
              <FieldHint tone="error">
                Date of Occurrence cannot be in the future. Choose today or an earlier date.
              </FieldHint>
            )}
            {submitted && !dateOfOccurrence && (
              <FieldHint tone="error">Date of Occurrence is required.</FieldHint>
            )}
          </FieldGroup>
        </FieldGrid>

        <FieldGrid>
          <FieldGroup>
            <FieldLabel htmlFor="issue-category" required>Issue Category</FieldLabel>
            <SelectField id="issue-category" value={issueCategory} onChange={(e) => setIssueCategory(e.target.value)} placeholderValue="" hasError={submitted && !issueCategory}>
              <option value="">Select category…</option>
              {intakeConfig.issue_categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </SelectField>
            {submitted && !issueCategory && <FieldHint tone="error">Please select an issue category.</FieldHint>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="affected-system" required>Affected System / Process</FieldLabel>
            <SelectField id="affected-system" value={affectedSystem} onChange={(e) => setAffectedSystem(e.target.value)} placeholderValue="" hasError={submitted && !affectedSystem}>
              <option value="">Select system…</option>
              {intakeConfig.affected_systems.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </SelectField>
            {submitted && !affectedSystem && <FieldHint tone="error">Please select an affected system.</FieldHint>}
          </FieldGroup>
        </FieldGrid>

        <FieldGrid>
          <FieldGroup>
            <FieldLabel htmlFor="user-priority" required>Priority</FieldLabel>
            <SelectField id="user-priority" value={userPriority} onChange={(e) => setUserPriority(e.target.value)} placeholderValue="" hasError={submitted && !userPriority}>
              <option value="">Select priority…</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </SelectField>
            {submitted && !userPriority && <FieldHint tone="error">Please select a priority.</FieldHint>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="action-requested" required>Action Requested</FieldLabel>
            <SelectField id="action-requested" value={actionRequested} onChange={(e) => setActionRequested(e.target.value)} placeholderValue="" hasError={submitted && !actionRequested}>
              <option value="">Select action…</option>
              {intakeConfig.action_requested_options.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </SelectField>
            {submitted && !actionRequested && <FieldHint tone="error">Please select an action.</FieldHint>}
          </FieldGroup>
        </FieldGrid>
      </FormSection>

      <FormSection>
        <FieldGroup>
          <FieldLabel htmlFor="problem-description" required>Problem Description</FieldLabel>
          <FieldHint>{promptText}</FieldHint>
          <TextareaField
            id="problem-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            hasError={descOverLimit || (submitted && !description.trim())}
            className="min-h-64 sm:min-h-72"
            placeholder="Describe the issue in as much detail as possible…"
          />
          {submitted && !description.trim() && !descOverLimit && (
            <FieldHint tone="error">A description is required.</FieldHint>
          )}
          <FieldHint tone={descOverLimit ? 'error' : 'neutral'}>
            {descOverLimit ? `${Math.abs(descRemaining)} characters over limit` : `${descRemaining} characters remaining`}
          </FieldHint>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="success-criteria" required>What does success look like?</FieldLabel>
          <TextareaField
            id="success-criteria"
            value={successCriteria}
            onChange={(e) => setSuccessCriteria(e.target.value)}
            rows={3}
            hasError={successOverLimit || (submitted && !successCriteria.trim())}
            placeholder="Describe the outcome that would resolve this issue…"
          />
          {submitted && !successCriteria.trim() && !successOverLimit && (
            <FieldHint tone="error">Please describe what success looks like.</FieldHint>
          )}
          <FieldHint tone={successOverLimit ? 'error' : 'neutral'}>
            {successOverLimit ? `${Math.abs(successRemaining)} characters over limit` : `${successRemaining} characters remaining`}
          </FieldHint>
        </FieldGroup>
      </FormSection>

      {error && <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>}

      <FormActions>
        <PrimaryButton
          type="submit"
          disabled={loading}
          className="w-full"
        >
          {loading ? 'Submitting…' : 'Submit issue'}
        </PrimaryButton>
      </FormActions>
    </FormPanel>
  )
}
